import React from 'react'
import { Image, Text, View, Page, Document, StyleSheet, Svg, Line, Font } from '@react-pdf/renderer';
import logo from '../../../assets/images/brand/login_logo.png';
import { NumericFormat } from 'react-number-format';

const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
};

const capitalizeWords = (str) =>
    str.replace(/\b\w/g, char => char.toUpperCase());

const formattedAmount = (amount) =>
    new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);

const fields = [
    { label: "Make & Model", key: "make_model" },
    { label: "Commonly Called", key: "commonly_called" },
    { label: "Grade", key: "grade" },
    { label: "Chassis Number", key: "chassis_no" },
    { label: "Engine Number", key: "engine_no" },
    { label: "Year", key: "reg_year" },
    { label: "Body Color", key: "body_color" },
    { label: "Status", key: "registration_status" },
    { label: "Country of Origin", key: "country_of_origin" },
    { label: "Selling Price", key: "selling_price" },
    { label: "Loan or Lease Amount", key: "lease_or_loan_amount" },
];

const InvoicePDF = ({ data }) => {

    Font.register({
        family: 'Open Sans',
        src: require('../../../assets/fonts/OpenSans-Regular.ttf')
    });
    Font.register({
        family: 'Open Sans Semi Bold',
        src: require('../../../assets/fonts/OpenSans-SemiBold.ttf')
    });
    Font.register({
        family: 'Open Sans Bold',
        src: require('../../../assets/fonts/OpenSans-Bold.ttf')
    });

    const styles = StyleSheet.create({
        page: { fontSize: 10, paddingTop: 20, paddingLeft: 40, paddingRight: 40, lineHeight: 1.5, flexDirection: 'column', fontFamily: 'Open Sans' },

        spaceBetween: { flex: 1, flexDirection: 'row', alignItems: 'top', justifyContent: 'space-between', color: "#3E3E3E" },

        titleContainer: { flexDirection: 'row', marginTop: 24, marginBottom: 10 },

        subTitleContainer: { flexDirection: 'column', gap: 15, marginBottom: 8, height: 100 },

        tableContainer: { flexDirection: 'row', marginTop: 5, marginBottom: 5 },

        logo: { width: 150, height: 'auto', marginBottom: 25 },

        addressTitle: {
            fontSize: 10, textAlign: 'right', marginBottom: 0, marginTop: 0, lineHeight: 1.2,
        },

        invoice: { fontSize: 16, marginBottom: 20, marginTop: 0, lineHeight: 1.2, textAlign: 'center', fontFamily: 'Open Sans Semi Bold' },

        invoiceText: { fontSize: 10, marginBottom: 20 },

        address: { fontSize: 8, textAlign: 'right' },

        subFooterSectionData: {
            textAlign: 'center',
            marginTop: 100
        },

        subFooterTitle: {
            fontSize: 8,
            display: 'flex', 
            lexDirection: 'row'
        },

        footerSectionData: {
            position: 'absolute',
            bottom: 30,
            right: 40, display: 'flex', lexDirection: 'row', alignItems: 'flex-end', marginTop: 10
        },
        table: {
            width: "100%",
        },
        tableRow: {
            flexDirection: "row",
        },
        labelCell: {
            width: "30%",
        },
        valueCell: {
            width: "60%",
            fontFamily: 'Open Sans Semi Bold',
            fontSize: 10,
        },
        headerText: {
            fontWeight: "bold",
            fontSize: 10,
        },

        bankDetails: {
            marginTop: 80
        },

        subFooterEmail: {
            color: 'blue'
        }
    });

    const today = new Date();

    const formattedDate = formatDate(data[0]?.date? data[0]?.date : today);

    const InvoiceTitle = () => (
        <View style={styles.titleContainer}>
            <View style={styles.logoSec}>
                <Image style={[styles.logo, { right: 0 }]} src={logo} />
            </View>
        </View>
    );

    const Address = () => (
        <View style={styles.subTitleContainer}>
            <View>
                <Text style={styles.customerInfo}>{formattedDate}</Text>
                <Text style={styles.customerInfo}>Ref No: {data[0]?.document_ref  || 'xxxx xxxxxxx'}</Text>
            </View>
            <View>
                <Text style={styles.customerInfo}>{data[0]?.recipient_designation || 'xxxx xxxxxxx'}</Text>
                <Text style={styles.customerInfo}>{data[0]?.recipient_organization || 'xxxx xxxxxxx'}</Text>
                <Text style={styles.customerInfo}>{data[0]?.organization_branch || 'xxxx xxxxxxx'}</Text>
            </View>
        </View>
    );

    const Transaction = () => (
        <>
            <Text style={styles.invoice}>Invoice</Text>
            <Text style={styles.invoiceText}>I hereby forward the invoice for selling below said vehicle.</Text>
            <View style={styles.table}>
                {fields.map((item, index) => (
                    <View key={index} style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text style={styles.headerText}>{item.label}</Text>
                        </View>
                        <View style={styles.valueCell}><Text>
                            :{" "}
                            {item.key === "make_model"
                                ? `${data[1]?.make?.name || ""} ${data[1]?.model?.name || ""}`
                                : item.key === "country_of_origin"
                                    ? data[1]?.country?.name || ""
                                    :  item.key === "selling_price"
                                    ? `Rs ${[formattedAmount(data[1]?.selling_price)]}` || ""
                                :item.key === "lease_or_loan_amount"
                                    ? `Rs ${[formattedAmount(data[1]?.lease_or_loan_amount)]}` || ""
                                : data[1]?.[item.key]}
                        </Text>
                        </View>
                    </View>
                ))}
            </View>
        </>
    );

    const BankDetails = () => (
        <View style={styles.bankDetails}>
            <View style={styles.tableRow}>
                <View style={styles.labelCell}>
                    <Text style={styles.headerText}>Beneficiary</Text>
                </View>
                <View style={styles.valueCell}>
                    <Text>
                        : Meegalla Holdings (Pvt) Ltd. (Pv-109188)
                    </Text>
                    <Text>{" "}Acc No: 021810005901 Sampath Bank, Borella Super</Text>
                </View>
            </View>

            <View style={[styles.tableRow, { marginTop: 10 }]}>
                <View style={styles.labelCell}>
                    <Text style={styles.headerText}>Selling To</Text>
                </View>
                <View style={styles.valueCell}>
                    <Text>
                        : {data[0]?.bill_to_name  || 'xxxx xxxxxxx'}, {data[0]?.bill_to_address  || 'xxxx xxxxxxx'}</Text>
                </View>
            </View>
        </View>
    );

    const SubFooterData = () => (
        <View style={styles.subFooterSectionData}>
            <Text style={styles.subFooterTitle}>Meegalla Holdings Pvt. Ltd, 10, Wijerama Road, Colombo 07</Text>
            <Text style={styles.subFooterTitle}>TEL: +94 11 286 3410, EMAIL: <Text style={[styles.subFooterTitle, styles.subFooterEmail]}>meegallaholdings@gmail.com</Text></Text>
        </View>
    );

    const FooterData = () => (
        <View style={styles.footerSectionData}>
            <Text style={styles.addressTitle}>www.mholdings.lk</Text>
        </View>
    );
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <InvoiceTitle />
                <Address />
                <Transaction />
                <BankDetails />
                <SubFooterData />
                <FooterData />
            </Page>
        </Document>

    )
}

export default InvoicePDF